import { ControlEventTypes } from "Core/Events/EventTypes";

import { IODataResult, IDataRow } from "Entities";
import { BaseDropDownControl } from "Controls/BaseDropDownControl";
import { TextBlockService, DropDownService } from "Service";
import { Navigation, PageContext } from "Core";
import { TextBlockCategory } from "Enums";
import { ControlDefinition, DataRequestOption, Environment } from "Service/Client";

/**
 * Control for selecting multiple values from a dropdown list.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a MultiSelect with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class MultiSelect extends BaseDropDownControl {
    private _kendoElement: kendo.ui.MultiSelect;

    /**
     * @hidden
     */
    protected defaultEditorElement: string = "<select />";

    /**
     * @hidden
     */
    protected dmsControlClass = "dms-control-multiselect";

    /**
     * @hidden
     */
    protected bindingProperty: string = this.controlDefinition.LoadDataAsynchronously
        ? "deferredMultiSelectValueAsync"
        : "deferredMultiSelectValue";

    protected additionalBinding =
        this.controlDefinition.TextColumnName && this.controlDefinition.ValueColumnName != this.controlDefinition.TextColumnName
            ? "listTextMultiSelect: " + this.controlDefinition.TextColumnName
            : "";

    /**
     * @hidden
     */
    private dataSourceOptions: kendo.data.DataSourceOptions;
    private data: IODataResult;
    private valueIndex: number[] = null; // stored index for initial value selection
    private comboboxService: DropDownService;

    private valueBeforeBinding: string;

    /**
     * Create a new MultiSelectControl.
     * @param controlDefinition ControlDefinition.
     * @param comboboxService ComboboxService.
     * @param textBlockService TextBlockService.
     * @hidden
     */
    constructor(
        controlDefinition: ControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        environment: Environment,
        comboBoxService: DropDownService,
    ) {
        super(controlDefinition, textBlockCategory, textBlockService, environment);
        this.comboboxService = comboBoxService;
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));

        let dropDownOptions = this.getDropDownOptions();

        let options: kendo.ui.MultiSelectOptions = {
            dataValueField: this.controlDefinition.CommandValueColumnName,
            dataTextField: this.controlDefinition.CommandTextColumnName,
            tagTemplate: `#= get("${this.controlDefinition.CommandTextColumnName}") || "" #`,
            height: 290,
            autoBind: false,
            valuePrimitive: true,
            filter: "contains",
            dataBound: (e: kendo.ui.MultiSelectDataBoundEvent) => this.onDataBound(e),
            change: (e: kendo.ui.MultiSelectChangeEvent) => this.onChange(e),
            noDataTemplate: dropDownOptions.noDataTemplate,
            itemTemplate: dropDownOptions.template,
            headerTemplate: dropDownOptions.headerTemplate,
            autoWidth: this.controlDefinition.AutoWidth || false,
            footerTemplate: `<div class="dms-dropdown-footer k-widget k-header">#: instance.dataSource.total() # ${this.textBlockService.get(
                "ComboboxItemsLabel",
                TextBlockCategory.global,
            )}</div>`,
            maxSelectedItems: this.controlDefinition.MaxLength || null,
        };
        (<any>options).separator = this.controlDefinition.Mask || ",";

        if (this.controlDefinition.LoadDataAsynchronously) {
            this.dataSourceOptions = {
                transport: {
                    read: (options: kendo.data.DataSourceTransportReadOptions) => {
                        // check if loaded data contains the requested page
                        if (this.data && this.data.value) {
                            let value = this.data.value;
                            if (value[options.data.skip]) {
                                let data = value[options.data.skip];
                                delete value[options.data.skip];
                                this.valueBeforeBinding = this._kendoElement.value();

                                options.success({
                                    value: data,
                                    "odata.count": this.data["odata.count"],
                                });
                                return;
                            }
                        }

                        let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                        let requestOptions = this.getDataRequest();
                        requestOptions.SearchValue = null;
                        if (options.data.filter && options.data.filter.filters.length > 0) {
                            let item = <kendo.data.DataSourceFilterItem>options.data.filter.filters[0];
                            requestOptions.SearchText = item.value;
                        }
                        requestOptions.Skip = options.data.skip;

                        this.comboboxService
                            .get(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow)
                            .then((result: IODataResult) => {
                                if (result.error) {
                                    Navigation.errorAsync(result.error, "Error");
                                    return;
                                }

                                options.success({
                                    value: result.value[0],
                                    "odata.count": result["odata.count"],
                                });
                            });
                    },
                },
                schema: {
                    data: function (data: any) {
                        return data["value"];
                    },
                    total: function (data: any) {
                        return data["odata.count"];
                    },
                },
                // error: (e) => this.onError(e),
                serverPaging: true,
                serverFiltering: true,
                pageSize: this.controlDefinition.ItemsPerRequest > 0 ? this.controlDefinition.ItemsPerRequest : 10,
            };

            let asyncOptions: kendo.ui.MultiSelectOptions = {
                height: ((this.controlDefinition.ItemsPerRequest > 0 ? this.controlDefinition.ItemsPerRequest : 10) / 4) * 30,
                virtual: {
                    itemHeight: 30,
                    valueMapper: (options: any) => {
                        if (this.valueIndex !== undefined) {
                            options.success(this.valueIndex);
                            this.valueIndex = undefined;
                            return;
                        }

                        if (!options.value) {
                            options.success([]);
                            return;
                        }

                        this.data = null;
                        let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                        let requestOptions: DataRequestOption = {
                            ControlID: this.controlDefinition.ControlID,
                            SearchValue: options.value != null ? options.value : [],
                        };
                        this.comboboxService
                            .mapValue(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow)
                            .then((result) => {
                                if (result.Error) {
                                    Navigation.errorAsync(result.Error, "Error");
                                    return;
                                }

                                options.success(result.selectedIndex);
                                if (result.selectedIndex.length == 0) {
                                    this._kendoElement.value([]);
                                    this._kendoElement.trigger("change");
                                }
                            });
                    },
                },
            };

            $.extend(options, asyncOptions);
        } else {
            this.dataSourceOptions = {
                transport: {
                    read: (options: kendo.data.DataSourceTransportRead) => {
                        if (this.data != null) {
                            this.valueBeforeBinding = this._kendoElement.value();
                            (options.success as JQuery.Ajax.SuccessCallback<any>)(this.data, null, null);
                            return;
                        }
                    },
                },
                schema: {
                    data: function (data: IODataResult) {
                        return data.value[0];
                    },
                    total: function (data: IODataResult) {
                        return data["odata.count"];
                    },
                },
                // error: (e) => this.onError(e)
            };
        }

        this._kendoElement = new kendo.ui.MultiSelect(this.jQueryElement[0], options);
        this.dataSource = this._kendoElement.dataSource;
        (<any>this._kendoElement)._filterSource = this.getFilterSource(this._kendoElement.options.dataTextField);

        if (dropDownOptions.setListWidth) {
            let listWidth = dropDownOptions.listWidth;
            if (listWidth > this._kendoElement.element.width()) {
                this._kendoElement.popup.element.width(listWidth);
            }
        }
    }

    /**
     * Format the value with highlighting from text search.
     * @param value Value to be formatted.
     * @hidden
     */
    public formatValue(value: string): string {
        let text = this._kendoElement.input.val() as string;
        // let items = this._kendoElement.dataItems();
        if (!text || !value /*|| items.length == 0*/) {
            return value;
        }
        if (this.lastText != text) {
            // todo: refactor replace to own service.
            this.textMatcher = new RegExp(text.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"), "ig");
            this.lastText = text;
        }
        return value.toString().replace(this.textMatcher, function (match) {
            return "<em>" + match + "</em>";
        });
    }

    /**
     * Get the selected values in comma separated string format.
     * @since 4.0.0
     */
    public getValue(): string {
        let val = this._kendoElement.value();
        if ((val && val.length == 0) || !val) {
            return null;
        } else if (val) {
            return val.join((<any>this._kendoElement.options).separator);
        }
    }

    /**
     * Get the selected values as array.
     * @since 4.0.0
     * @returns Items values as array. Empty array if no items are selected.
     */
    public getValueArray(): string[] {
        return this._kendoElement.value();
    }

    /**
     * Set the selected value as comma separated string.
     * @param value Value to be set.
     * @since 4.0.0
     */
    public setValue(value: string) {
        let oldValue = this.getValue();
        if (oldValue != value) {
            let items = value ? value.split((<any>this._kendoElement.options).separator) : null;
            this._kendoElement.value(items);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the kendo multiselect control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/multiselect|here} for the api definition.
     * @since 4.0.2
     */
    public getKendoElement(): kendo.ui.MultiSelect {
        return this._kendoElement;
    }

    /**
     * Get the text of the selected items.
     * @since 4.0.0
     */
    public getText(): string {
        let dataItems = this._kendoElement.dataItems();
        let text = "";
        let notFirst: boolean = false;
        for (let i = 0, len = dataItems.length; i < len; i++) {
            if (notFirst) {
                text += ", ";
            }
            text += dataItems[i][this.controlDefinition.CommandTextColumnName];
            notFirst = true;
        }
        return text;
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if the control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.input.prop("disabled");
    }

    /**
     * Handler for change-event.
     * @param e EventArgs.
     */
    private onChange(e: kendo.ui.MultiSelectChangeEvent): void {
        // multiselect control uses the valuemapper after blur if a new value is selected.
        // if the valueIndex is still set, the valuemapper returns wrong selection.
        this.valueIndex = undefined;
        this.trigger(ControlEventTypes.Change);
        PageContext.resizePage();
    }

    /**
     * Occurs after data was bound on the control.
     * @param e EventArgs.
     */
    private onDataBound(e: kendo.ui.MultiSelectDataBoundEvent): void {
        this.setBusy(false);

        if (this.controlDefinition.LoadDataAsynchronously) {
            if (this.valueBeforeBinding !== undefined) {
                // if the value was changed during binding, trigger "change" for dependent controls.
                let value = this._kendoElement.value();
                if (value.length != this.valueBeforeBinding.length || (value && !value.every((v: string, i: string) => v === value[i]))) {
                    this._kendoElement.trigger("change");
                    this.valueBeforeBinding = undefined;
                }
                this.setDataPromiseResolve();
            }
        }

        this.trigger(ControlEventTypes.DataBound);
    }

    /**
     * Get info for requesting data.
     * @hidden
     */
    public getDataRequest(): DataRequestOption {
        if (this._kendoElement == null) {
            return null;
        }
        let result: DataRequestOption = {
            ControlID: this.controlDefinition.ControlID,
            SearchValue: this._kendoElement.element.val() as string[],
            Take: this.controlDefinition.LoadDataAsynchronously ? this.controlDefinition.ItemsPerRequest || 10 : null,
            Skip: null,
            FilterOperator: null,
            SearchText: null,
        };
        return result;
    }

    private setDataPromise: Promise<void>;
    private setDataPromiseResolve: any;

    /**
     * Set the odata-result to the control.
     * @param data Data to be set.
     * @hidden
     */
    public setData(data: IODataResult): Promise<void> {
        this.data = data;

        if (this.controlDefinition.LoadDataAsynchronously) {
            // the loading of the virtual combobox continues after returning above methods.
            // therefore we need to wait for the promise to be resolved in the onDataBound handler
            this.setDataPromise = new Promise((resolve, rject) => {
                this.setDataPromiseResolve = resolve;
            });

            let value = this._kendoElement.value();
            this.valueIndex = data.selectedIndex;
            this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
            this.dataSource = this._kendoElement.dataSource;
            this._kendoElement.value(value);

            if (value && value.length == 0) {
                this._kendoElement.dataSource.read();
            }

            return this.setDataPromise;
        } else {
            this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
            this.dataSource = this._kendoElement.dataSource;
            this._kendoElement.dataSource.read();
            return Promise.resolve();
        }
    }

    /**
     * Select the item at index.
     * @param index The zero-based index of the item which should be selected.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // in script:
     * @@ctr:1337@@.selectItemAt(5);
     *
     * // on page:
     * $dms(1337).selectItemAt(5);
     * ```
     */
    public selectItemAt(index: number): void {
        if (index < 0) {
            this._kendoElement.value([]);
        } else {
            let value = [this._kendoElement.dataSource.data()[index][this.controlDefinition.CommandValueColumnName]];
            this._kendoElement.value(value);
        }
        this._kendoElement.trigger("change");
    }

    /**
     * Returns true if an item with given value is in the dropdownlist.
     * @param value The value to be searched.
     * @since 4.0.0
     * @returns True if the value is in the list of available values.
     * @example
     * ```JavaScript
     * @@ctr:1337@@.containsValue("1");
     * ```
     */
    public containsValue(value: string): boolean {
        let list = this._kendoElement.dataSource.data();
        let columnName = this.controlDefinition.CommandValueColumnName;
        for (let i = 0, len = list.length; i < len; i++) {
            if (list[i][columnName] == value) {
                return true;
            }
        }
        return false;
    }

    /**
     * Select the item which text contains given search string.
     * @param text The text to be searched.
     * @since 4.0.0
     */
    public selectItemWithText(text: string): void {
        let data = this._kendoElement.dataSource.data();
        for (let i = 0, len = data.length; i < len; i++) {
            if (
                data[i][this.controlDefinition.CommandTextColumnName] &&
                data[i][this.controlDefinition.CommandTextColumnName].toString().indexOf(text) !== -1
            ) {
                this.selectItemAt(i);
                return;
            }
        }
        // no item was found -> unselect values
        this.selectItemAt(-1);
    }

    /**
     * Set the search filter for the combobox.
     * @param text Text to be searched.
     * @since 4.0.2
     */
    public setSearch(text: string): void {
        this._kendoElement.search(text);
    }

    /**
     * Show loading indicator on the control.
     * @param isBusy True if loading indicator should be shown.
     * @since 4.0.0
     */
    public setBusy(isBusy: boolean): void {
        let element = this.getOuterElement().find(".k-svg-i-caret-alt-down");
        if (isBusy) {
            element.removeClass("k-hidden");
        } else {
            element.addClass("k-hidden");
        }
    }

    /**
     * Reload the control items with data from the server.
     * @since 5.0.0
     */
    public reload(): Promise<void> {
        return this.execute<Promise<void>>(ControlEventTypes.ControlReloadRequested);
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        try {
            if (this._kendoElement != null) {
                this._kendoElement.destroy();
            }

            if (this.jQueryElement) {
                this.jQueryElement.off("focusout");
            }
        } catch (error) {}
    }
}
